import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/navigation/Header';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location?.state?.orderData;

  useEffect(() => {
    // If no order data, redirect back to purchase page
    if (!orderData) {
      navigate('/purchase-credits');
    }
  }, [orderData, navigate]);

  if (!orderData) return null;

  const {
    transactionId,
    packageInfo,
    subtotal,
    tax,
    total,
    email,
    paymentMethod,
    date,
    creditsAdded,
  } = orderData;

  const handleDownloadReceipt = () => {
    const receiptContent = `
===========================================
           ANGLE! - PURCHASE RECEIPT
===========================================

Transaction ID: ${transactionId}
Date: ${date}

-------------------------------------------
ORDER DETAILS
-------------------------------------------
Package: ${packageInfo?.name}
Credits: ${creditsAdded || packageInfo?.credits}
Price: $${subtotal?.toFixed(2)}

-------------------------------------------
PRICE BREAKDOWN
-------------------------------------------
Total: $${total?.toFixed(2)}

-------------------------------------------
PAYMENT INFORMATION
-------------------------------------------
Payment Method: ${paymentMethod}
Email: ${email}

-------------------------------------------
THANK YOU FOR YOUR PURCHASE!
-------------------------------------------
Your credits have been added to your account.
Start analyzing your photos now!

Support: support@notifications.angle.dating
===========================================
    `;
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Angle_Receipt_${transactionId}.txt`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-cyan-50">
      <Header
        user={null}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => navigate('/purchase-credits')}
        onLogout={() => navigate('/')}
      />
      <div className="flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Success animation header */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 18 }}
          className="text-center mb-8"
        >
          <div className="relative inline-flex items-center justify-center mb-5">
            {/* Outer ring */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1.3, opacity: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="absolute w-24 h-24 rounded-full bg-emerald-400"
            />
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-200">
              <Icon name="CheckCircle" size={44} color="#ffffff" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-500 text-base">
            Your purchase was successful and credits have been added to your account.
          </p>
        </motion.div>

        {/* Receipt card */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5, ease: 'easeOut' }}
          className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
        >
          {/* Top stripe */}
          <div className="h-2 bg-gradient-to-r from-purple-600 via-violet-500 to-cyan-500" />

          <div className="p-6 md:p-8">
            {/* Transaction ID */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center border border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Transaction ID</p>
              <p className="text-sm font-mono font-semibold text-gray-800">{transactionId}</p>
              <p className="text-xs text-gray-400 mt-1">{date}</p>
            </div>

            {/* Package purchased */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Package Purchased
              </p>
              <div className="flex items-center gap-4 bg-gradient-to-r from-purple-50 to-cyan-50 rounded-xl p-4 border border-purple-100">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md shadow-purple-200">
                  <Icon name="Zap" size={24} color="#ffffff" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg leading-tight">{packageInfo?.name}</h3>
                  <p className="text-sm text-gray-500">{creditsAdded || packageInfo?.credits} photo analysis credits</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent">
                    ${subtotal?.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Credits added badge */}
            <div className="flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl py-3 px-4 mb-6">
              <Icon name="Zap" size={18} color="#10B981" />
              <span className="text-sm font-semibold text-emerald-700">
                {creditsAdded || packageInfo?.credits} credits added to your account
              </span>
            </div>

            {/* Price breakdown */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Price Breakdown
              </p>
              <div className="space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total</span>
                  <span className="text-gray-800 font-medium">${total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment info */}
            <div className="mb-8">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Payment Information
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="CreditCard" size={15} color="#9CA3AF" />
                  <span className="text-gray-500">Method:</span>
                  <span className="text-gray-800 font-medium">{paymentMethod}</span>
                </div>
                {email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Mail" size={15} color="#9CA3AF" />
                    <span className="text-gray-500">Receipt sent to:</span>
                    <span className="text-gray-800 font-medium truncate">{email}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                variant="default"
                fullWidth
                onClick={() => navigate('/user-dashboard')}
                iconName="LayoutDashboard"
                iconPosition="right"
                className="h-12 text-base font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700"
              >
                Go to Dashboard
              </Button>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/photo-upload-interface')}
                  iconName="Camera"
                  iconPosition="left"
                  className="h-11 text-sm"
                >
                  Rank Photos
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadReceipt}
                  iconName="Download"
                  iconPosition="left"
                  className="h-11 text-sm"
                >
                  Download Receipt
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stripe security note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex items-center justify-center gap-2 mt-5 text-gray-400"
        >
          <Icon name="ShieldCheck" size={15} color="#9CA3AF" />
          <span className="text-xs">Payment processed securely by Stripe</span>
        </motion.div>
      </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
